import React, { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Booth } from '../../../../../interfaces/interfaces';
import { updateBooth } from '../../../../../services/services';
import VyButton from '../../../../common/VyButton';
import VyModal from '../../../../common/VyModal';
import { DragCard } from '../../../../common/DragCard';

interface SlideEditModalProps {
  booth: Booth;
  onUpdate?: () => void;
  onClose?: () => void;
}

export const SlideEditModal = ({ booth, onUpdate, onClose }: SlideEditModalProps) => {
  const [slides, setSlides] = useState<{ id: number; img: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation(['common', 'booth']);

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const newCards = Array.from(slides);
    const [reorderedItem] = newCards.splice(result.source.index, 1);
    newCards.splice(result.destination.index, 0, reorderedItem);

    setSlides(newCards);
  };

  function formatSlides() {
    return (
      booth?.headerImages?.map((img, i) => ({
        id: i,
        img,
      })) ?? []
    );
  }

  function handleDelete(id: string) {
    setSlides((prev) => prev.filter((slide) => slide.img !== id));
  }

  const handleSubmit = async () => {
    setIsLoading(true);
    await updateBooth({
      boothid: booth._id,
      headerImages: slides.map((slide) => slide.img),
    });
    onUpdate?.();
    setIsLoading(false);
    onClose?.();
  };

  useEffect(() => {
    setSlides(formatSlides());

    return () => {
      setSlides([]);
    };
  }, [booth]);

  return (
    <VyModal open={true} hasDefaultPadding={false} maxWidth="1000px" onSubmit={handleSubmit} onClose={onClose}>
      {(onClose, onSubmit) => (
        <div className="flex flex-col gap-2 p-4">
          <p className="text-2xl">{t('booth:edit-slides')}</p>
          <p className="text-slate-600">{t('booth:edit-slides-info')}</p>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="video-list">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} style={{ minHeight: 50 }} className="flex relative flex-col gap-2">
                  {slides.map((slide, index) => (
                    <Draggable key={slide.id} draggableId={slide.id.toString()} index={index}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="relative">
                          <DragCard id={slide.id} img={slide.img} index={index} onDelete={() => handleDelete(slide.img)} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="flex flex-col gap-2 justify-between sm:flex-row">
            <VyButton className="w-full" onClick={onClose} disabled={isLoading} ghost>
              {t('common:cancel')}
            </VyButton>
            <VyButton className="w-full" onClick={onSubmit} showLoadingIcon={isLoading} disabled={isLoading}>
              {t('common:confirm')}
            </VyButton>
          </div>
        </div>
      )}
    </VyModal>
  );
};
